import React, { useState } from "react";
import { Search, Loader2 } from "lucide-react";
import client from "@/lib/api";
import { inp, err } from "@/components/conviction/studioShared";

const short = (a) => (a && a.length > 14 ? `${a.slice(0, 6)}…${a.slice(-4)}` : a);
const usd = (v) => (v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `$${(v / 1e3).toFixed(0)}K` : `$${v}`);

export function AssetSummary({ a }) {
  if (!a) return null;
  return (
    <div data-testid="asset-selected" className="rounded-xl border border-[#ccff00]/30 bg-[#ccff00]/[0.04] p-3 text-sm">
      <p className="font-semibold">{a.ticker} <span className="text-zinc-400 font-normal">{a.name}</span></p>
      <p className="text-xs text-zinc-400 mt-0.5">{a.asset_class === "stock" ? "Stock" : a.venue === "DEX" ? "Crypto · DEX" : "Crypto · CEX"} · {a.exchange}{a.network ? ` · ${a.network}` : ""} · quote {a.quote}{a.contract_address ? ` · ${short(a.contract_address)}` : ""}</p>
    </div>
  );
}

export function AssetPicker({ onPick, testid = "asset-picker" }) {
  const [q, setQ] = useState("");
  const [cls, setCls] = useState("all");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(null);
  const search = async () => {
    if (!q.trim()) return;
    setBusy(true);
    try { setRes((await client.get("/v2/assets/search", { params: { q: q.trim(), cls } })).data); } catch (e) { err(e); } finally { setBusy(false); }
  };
  const pick = async (r) => {
    setPicking(r.provider_symbol);
    try { onPick((await client.post("/v2/assets/resolve", { provider: r.provider, provider_symbol: r.provider_symbol })).data); setRes(null); setQ(""); } catch (e) { err(e); } finally { setPicking(null); }
  };
  return (
    <div data-testid={testid} className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {[["all", "All"], ["crypto", "Crypto"], ["stock", "Stocks"]].map(([k, l]) => (
          <button key={k} type="button" data-testid={`ap-cls-${k}`} onClick={() => setCls(k)} className={`px-3 py-1.5 rounded-full text-xs ${cls === k ? "bg-[#ccff00] text-black font-semibold" : "border border-white/15 text-zinc-300"}`}>{l}</button>))}
      </div>
      <div className="flex gap-2">
        <input data-testid="ap-query" className={inp} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), search())} placeholder="Ticker, name or contract address (e.g. HYPE, MSTR, PEPE)" />
        <button type="button" data-testid="ap-search" onClick={search} className="px-4 rounded-xl border border-white/15 text-sm inline-flex items-center gap-2">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}Search</button>
      </div>
      {res && (
        <div data-testid="ap-results" className="rounded-xl border border-white/10 divide-y divide-white/5 max-h-80 overflow-y-auto">
          {res.results.length > 1 && <p className="px-3 py-2 text-xs text-amber-300" data-testid="ap-ambiguous">Several instruments match. Pick the exact one; nothing is selected automatically.</p>}
          {res.results.length === 0 && <p className="px-3 py-3 text-sm text-zinc-400" data-testid="ap-none">No instrument found on OKX, GeckoTerminal (DEX) or Yahoo Finance. Try the full name or contract address.</p>}
          {res.provider_errors?.map((e) => <p key={e} className="px-3 py-2 text-xs text-amber-300">{e}</p>)}
          {res.results.map((r) => (
            <button type="button" key={`${r.provider}:${r.provider_symbol}`} data-testid={`ap-result-${r.provider}-${r.provider_symbol}`} onClick={() => pick(r)} disabled={!!picking}
              className="w-full text-left px-3 py-2.5 hover:bg-white/[0.04] flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="font-semibold text-sm">{r.ticker}</span><span className="text-sm text-zinc-400 truncate max-w-[220px]">{r.name}</span>
              <span className="text-[10px] font-mono-cl uppercase px-1.5 py-0.5 rounded border border-white/15 text-zinc-400">{r.asset_class === "stock" ? `Stock · ${r.venue}` : r.venue}</span>
              <span className="text-xs text-zinc-500">{r.exchange}{r.network ? ` · ${r.network}` : ""}{r.quote ? ` · ${r.quote}` : ""}{r.pool ? ` · ${r.pool}` : ""}{r.contract_address ? ` · ${short(r.contract_address)}` : ""}</span>
              {r.liquidity_usd != null && <span className={`text-xs ${r.liquidity_usd < 50000 ? "text-amber-300" : "text-zinc-500"}`}>liquidity {usd(r.liquidity_usd)}{r.liquidity_usd < 50000 ? " (thin)" : ""}</span>}
              {r.asset_id && <span className="text-[10px] text-[#ccff00]">used before</span>}
              {picking === r.provider_symbol && <Loader2 className="w-3 h-3 animate-spin" />}
            </button>))}
        </div>
      )}
    </div>
  );
}
